import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import {Doughnut, Bar} from "react-chartjs-2";
import _ from "lodash";
import {CommandBarButton} from "office-ui-fabric-react";
import {DEFAULT_CHART_COLOURS} from "../pages/admin/workloads/WorkloadResultsCharts";
import {
    groupProcessorItems, ProcessorItem,
} from "../../models/MailroomProcessorItem";
import DispatchProps from "../../utils/DispatchProps";
import {IRootState} from "../../store";


interface ProcessorResultsChartsProps {
    processorItems?: ProcessorItem[];
    onCloseClicked(): void;
}

interface ProcessorResultsChartsState {
    statusDoughnutData: any;
    typeDoughnutData: any;
    percentageSuccessBarChartData: any;
    totalItemCount: number;
    errorItemCount: number;
    percentSuccess: string;
}


const BAR_CHART_OPTIONS = {
    scales: {
        yAxes: [
            {
                ticks: {
                    beginAtZero: true,
                    min: 0,
                    max: 100
                },

            },
        ],
    },
};


class ProcessorResultsCharts extends Component<ProcessorResultsChartsProps & DispatchProps, ProcessorResultsChartsState> {

    constructor(props: ProcessorResultsChartsProps & DispatchProps) {
        super(props);

        this.state = {
            statusDoughnutData: {},
            typeDoughnutData: {},
            percentageSuccessBarChartData: {},
            totalItemCount: 0,
            errorItemCount: 0,
            percentSuccess: ""
        };
    }

    componentDidMount(): void {
        this.setChartData();
    }

    async componentDidUpdate(prevProps: Readonly<ProcessorResultsChartsProps & DispatchProps>, prevState: Readonly<ProcessorResultsChartsState>, snapshot?: any): Promise<void> {

        if(this.props.processorItems !== prevProps.processorItems)
        {
            this.setChartData();
        }
    }

    private setChartData() {
        
        if(!this.props.processorItems)
        {
            return;
        }

        const itemsGroupedByStatus = _.groupBy(this.props.processorItems!,i => {
            if(i.error)
            {
                return "Errored";
            }
            else
            {
                return "Success";
            }
        });
        const statusLabels = ["Success", "Errored"]
        const statusData = statusLabels.map(status => itemsGroupedByStatus[status]?.length || 0);

        const statusDoughnutData = {
            title: "Status",
            labels: statusLabels,
            datasets: [{
                data: statusData,
                backgroundColor: [
                    '#99c140',
                    '#cc3232',
                ]
            }]
        };
        
        const errorCount = itemsGroupedByStatus["Errored"]?.length || 0;
        const successCount = itemsGroupedByStatus["Success"]?.length || 0;
        const totalCount = errorCount + successCount;
        const percentSuccessString = `${( (successCount / totalCount) * 100).toFixed(2)}%`;

        
        const groupedItemsResult = groupProcessorItems(this.props.processorItems!);
        const shortTypes = _.keys(groupedItemsResult.typesGroupedByShortType);

        const typeDoughnutData = {
            labels: shortTypes,
            datasets: [{
                label: "Processor Item Type",
                data: shortTypes.map(shortType => groupedItemsResult.itemsGroupedByShortType[shortType]?.length || 0),
                backgroundColor: DEFAULT_CHART_COLOURS
            }]
        };

        
        const typePercentageData = shortTypes.map(shortType => {
            const items = groupedItemsResult.itemsGroupedByShortType[shortType] || [];
            const erroredCount = _.filter(items,(i) => i.error).length;
            const successCount = items.length - erroredCount;
            return ( (successCount / items.length) * 100).toFixed(2);
        });
        
        const percentageSuccessBarChartData = {
            labels: shortTypes,
            datasets: [
                {
                    label: '% Success',
                    data: typePercentageData,
                    backgroundColor: DEFAULT_CHART_COLOURS
                },
            ],
        };
        

        this.setState({
            statusDoughnutData,
            typeDoughnutData,
            percentageSuccessBarChartData,
            errorItemCount: errorCount,
            totalItemCount: totalCount,
            percentSuccess: percentSuccessString
        });

    }

    render(): JSX.Element {
        return (
            <Fragment>
                <div className="card card">
                    <div className="card__header">
                        <span className={"card__title"}>Results</span>
                        <CommandBarButton
                            iconProps={{iconName: "Cancel"}}
                            text="Close"
                            onClick={this.props.onCloseClicked}
                        />
                    </div>
                    <div className="card__content flex-column">
                        <div className={"flex-row flex--justify-content-space-around"}>
                            <div className="doughnut-holder">
                                <Doughnut data={this.state.statusDoughnutData} height={1} width={1} />
                            </div>
                            <div className="doughnut-holder">
                                <Doughnut data={this.state.typeDoughnutData} height={1} width={1} />
                            </div>

                            <div className={"flex-column"}>
                                <div className="number-stat">
                                    <span className="number-stat__number number-stat__number--medium">{this.state.percentSuccess}</span>
                                    <span className="number-stat__description">
                                        Successful Items
                                    </span>
                                </div>
                                <div className="number-stat">
                                    <span className="number-stat__number number-stat__number--medium">{this.state.totalItemCount}</span>
                                    <span className="number-stat__description">
                                        Total Items 
                                    </span>
                                </div>
                                <div className="number-stat">
                                    <span className="number-stat__number number-stat__number--medium">{this.state.errorItemCount}</span>
                                    <span className="number-stat__description">
                                        Errored Items
                                    </span>
                                </div>

                            </div>

                        </div>
                        <div className={"flex-row"}>
                            <Bar data={this.state.percentageSuccessBarChartData} options={BAR_CHART_OPTIONS} height={1} width={4}/>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { processorItems?: ProcessorItem[],onCloseClicked: () => void }): ProcessorResultsChartsProps => {
        return {
            processorItems: ownProps.processorItems,
            onCloseClicked:ownProps.onCloseClicked
        };
    }
)(ProcessorResultsCharts)